import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { GET_DADOS_CNPJ_INIT } from '../actionTypes';
import {
  dadosExternosErrorMsg,
  getDadosCnpjSuccess,
} from './actions';
import axios from 'axios';
import { ICnpjDados } from 'src/integracoes/modelos/loja';
import { extrairNumeroEComplemento, textoCapitalizado } from 'src/utils/functions';

const getFromCnpja = async (cnpj: string): Promise<ICnpjDados> => {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  const response = await axios.get(`https://open.cnpja.com/office/${cnpjLimpo}`);
  const data = response.data;

  const telefone = data.phones?.[0]
    ? `(${data.phones[0].area}) ${data.phones[0].number}`
    : '';

  const tipo = data.head ? 'Matriz' : 'Filial';
  const nome = (data.company?.name ?? data.alias ?? '') + ` - ${tipo.toUpperCase()}`;

  const { numero, complemento } = extrairNumeroEComplemento(
    data.address?.number,
    data.address?.details
  );

  return {
    logradouro: textoCapitalizado(data.address?.street ?? ''),
    numero,
    bairro: textoCapitalizado(data.address?.district ?? ''),
    cep: data.address?.zip ?? '',
    complemento: textoCapitalizado(complemento),
    cidade: data.address?.city ?? '',
    estado: data.address?.state ?? '',
    telefone,
    nome: textoCapitalizado(nome),
  };
};

const getFromCnpjWs = async (cnpj: string): Promise<ICnpjDados> => {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  const response = await axios.get(`https://publica.cnpj.ws/cnpj/${cnpjLimpo}`);
  const data = response.data;

  const telefone = data.estabelecimento?.telefone1
    ? `(${data.estabelecimento.ddd1}) ${data.estabelecimento.telefone1}`
    : '';

  const tipo = data.estabelecimento?.tipo ?? '';
  const nome = (data.razao_social ?? '') + ` - ${tipo}`;

  const { numero, complemento } = extrairNumeroEComplemento(
    data.estabelecimento?.numero,
    data.estabelecimento?.complemento
  );

  return {
    logradouro: textoCapitalizado(
      `${data.estabelecimento?.tipo_logradouro ?? ''} ${data.estabelecimento?.logradouro ?? ''}`.trim()
    ),
    numero,
    bairro: textoCapitalizado(data.estabelecimento?.bairro ?? ''),
    cep: data.estabelecimento?.cep ?? '',
    complemento: textoCapitalizado(complemento),
    cidade: data.estabelecimento?.cidade?.nome ?? '',
    estado: data.estabelecimento?.estado?.sigla ?? '',
    telefone,
    nome: textoCapitalizado(nome),
  };
};

export const getCnpjAsync = async (
  cnpj: string
): Promise<{ success: boolean; dados?: ICnpjDados; msg: string }> => {
  try {
    // tenta na API mas rapida
    let dados = await getFromCnpja(cnpj);
    let msg = 'Dados obtidos da CNPJa';

    // verifica se campos importantes estao faltando
    const camposFaltando = !dados.telefone || !dados.logradouro || !dados.numero ||
      !dados.bairro || !dados.cidade || !dados.estado || !dados.cep;

    if (camposFaltando) {
      try {
        // busca dados da outra API
        const dadosExtra = await getFromCnpjWs(cnpj);
        // preenche apenas campos que estavam vazios
        dados = {
          ...dados, ...Object.fromEntries(
            Object.entries(dadosExtra).filter(([k, v]) => !dados[k as keyof ICnpjDados])
          )
        };
        msg += ' (completados pela CNPJ.ws)';
      } catch {
        // se der erro, ignora e segue só com o que conseguiu
      }
    }
    return { success: true, dados, msg };
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      if (status === 400) {
        return { success: false, msg: 'CNPJ não encontrado' };
      }
      // atingido na primeira API → tenta direto na segunda
      if (status === 429) {
        try {
          const dados = await getFromCnpjWs(cnpj);
          return { success: true, dados, msg: 'Dados obtidos da CNPJ.ws' };
        } catch (e: any) {
          if (axios.isAxiosError(e)) {
            if (e.response?.status === 429) {
              return { success: false, msg: 'Atingiu o limite de buscas' };
            }
            if (e.response?.status === 400) {
              return { success: false, msg: 'CNPJ não encontrado' };
            }
          }
          return { success: false, msg: 'Erro ao buscar CNPJ em ambas as fontes' };
        }
      }
    }
    return { success: false, msg: 'Falha ao obter dados do CNPJ' };
  }
};

function* getCnpjMiddle({ payload }: { payload: any, type: any }) {
  const { cnpj } = payload;
  try {
    const { success, msg, dados } = yield call(getCnpjAsync, cnpj);
    if (success) {
      yield put(getDadosCnpjSuccess({ msg, dados }));
    } else {
      yield put(dadosExternosErrorMsg({ msg }));
    }
  } catch (error: any) {
    yield put(dadosExternosErrorMsg({ msg: error.mensagem }));
  }
}

export function* handleGetCnpj() {
  yield takeEvery(GET_DADOS_CNPJ_INIT, getCnpjMiddle);
}

// WATCHS ------------------------------------------------
export default function* rootSaga() {
  yield all([fork(handleGetCnpj)]);
}
